// © Microsoft Corporation. All rights reserved.

// Map for storing the objects that with a key token
const propsMap: { [token: string]: any } = {};
let currentPropsToken = 1;

/**
 * Gets a token to point to a given props object. This can be passed to the props-token attribute on a web component element.
 * Use from React when you cannot directly set the props on child.
 * @param additionalProps The object to generate the token for.
 * @returns The token to point to the object.
 */
export function getPropsToken<T>(additionalProps: T): string {
    if (!additionalProps) {
        return;
    }

    for (const [token, storedPropsObject] of Object.entries(propsMap)) {
        if (storedPropsObject === additionalProps) {
            return token;
        }
    }

    const newToken = currentPropsToken.toString();
    propsMap[newToken] = additionalProps;
    currentPropsToken++;

    return newToken;
}

/**
 * Gets a props object from the given prop token.
 * @param propToken A prop token generated by getPropsToken.
 * @returns The props object for the given token.
 */
export function getPropObjectFromToken<T>(childPropToken: string): T {
    const props = propsMap[childPropToken];

    // Delete token and props in store after assigning data in WCE. Reduce the time of linear search and keep store slim.
    delete propsMap[childPropToken];

    return props;
}